import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  congesApi,
  type AnalyseImport,
  type SalarieReprise,
} from '../../services/conges-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  SaisieNombre,
  Saisie,
  Section,
  Selection,
  ValeurCalculee,
} from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { useToast } from '../../context/ToastContext';

/**
 * Reprise 2026 — la mise en service du module (R-CPT-12).
 *
 * Écran d'ADMINISTRATION, joué une seule fois. Sa méthode est la
 * reconstruction, pas la recopie de soldes : les salariés doivent retrouver ici
 * le DÉTAIL de leurs absences, sinon ils continueront d'ouvrir Airtable.
 *
 * Quatre étapes dans l'ordre, chacune vérifiable avant la suivante. La
 * quatrième — la vérification croisée — est le vrai filet : c'est le dernier
 * moment où une erreur de reprise reste rattrapable.
 */

function jours(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return v.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

export function CongesReprisePage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'reprise'],
    queryFn: () => congesApi.reprise.etat(),
  });

  if (isLoading) return <p className="p-6 text-slate-500">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;
  if (!data) return null;

  const rafraichir = () => qc.invalidateQueries({ queryKey: ['conges', 'reprise'] });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <header>
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Reprise 2026</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Les quatre étapes de la mise en service, dans l’ordre. La dernière, la comparaison avec
          les soldes Airtable, conditionne le passage en service : un écart non expliqué signifie
          qu’un salarié aura un solde faux.
        </p>
      </header>

      <EtapeReport salaries={data.salaries} onFait={rafraichir} />
      <EtapeReconstruction onFait={rafraichir} />
      <EtapeImport total={data.total_importees} onFait={rafraichir} />
      <EtapeControle salaries={data.salaries} arreteAu={data.arrete_au} onFait={rafraichir} />
    </div>
  );
}

// ─── 1. Report de congés payés ────────────────────────────────────────────

function EtapeReport({ salaries, onFait }: { salaries: SalarieReprise[]; onFait: () => void }) {
  const toast = useToast();
  // LECTURE d'abord, édition explicite : un écran qui s'ouvre en saisie ne dit
  // pas ce qui est enregistré et ce qui ne l'est pas (règle du skill ndf-ux).
  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState<Record<string, number | null>>({});
  const quitterEdition = () => {
    setEdition(false);
    setBrouillon({});
  };
  const enregistrer = useMutation({
    mutationFn: async () => {
      // Seuls les reports RÉELLEMENT modifiés partent : réécrire à l'identique
      // supprimerait puis recréerait un mouvement sans que rien n'ait changé.
      const modifs = Object.entries(brouillon).filter(([id, v]) => {
        const initial = salaries.find((s) => s.id === id)?.report ?? null;
        return v !== null && v !== initial;
      });
      for (const [id, v] of modifs) await congesApi.reprise.report(id, v as number);
      return modifs.length;
    },
    onSuccess: (n) => {
      toast.success(n === 0 ? 'Aucune modification.' : `${n} report(s) enregistré(s).`);
      quitterEdition();
      onFait();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.'),
  });

  return (
    <Section
      titre="1. Report de congés payés au 1er janvier 2026"
      description="Le solde Airtable au 31/12/2025, salarié par salarié. C’est la seule valeur qui ne peut pas se recalculer : elle vient de l’année précédente. Une resaisie remplace la précédente."
      actions={
        !edition ? (
          <BoutonSecondaire type="button" onClick={() => setEdition(true)}>
            Modifier
          </BoutonSecondaire>
        ) : (
          <div className="flex gap-2">
            <BoutonSecondaire type="button" onClick={quitterEdition}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              onClick={() => enregistrer.mutate()}
              disabled={enregistrer.isPending}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </div>
        )
      }
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-slate-600">
            <tr>
              <th className="py-2 pr-4 font-medium">Salarié</th>
              <th className="py-2 pr-4 font-medium">Report au 01/01/2026</th>
            </tr>
          </thead>
          <tbody>
            {salaries.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="py-2 pr-4 text-slate-800">
                  {s.prenom} {s.nom}
                  {s.forfait_jour && (
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      forfait jour
                    </span>
                  )}
                  {!s.compteurs_ouverts && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                      sans compteurs
                    </span>
                  )}
                  {s.motif_exclusion && (
                    <p className="mt-0.5 max-w-lg text-xs text-amber-800">{s.motif_exclusion}</p>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {edition ? (
                    <SaisieNombre
                      nullable
                      valeur={brouillon[s.id] ?? s.report}
                      onValeur={(v) => setBrouillon((b) => ({ ...b, [s.id]: v }))}
                      inputMode="decimal"
                      className="w-28"
                    />
                  ) : (
                    <ValeurCalculee>{s.report === null ? '-' : `${jours(s.report)} j`}</ValeurCalculee>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ─── 2. Reconstruction par les jobs ───────────────────────────────────────

function EtapeReconstruction({ onFait }: { onFait: () => void }) {
  const toast = useToast();
  const lancer = useMutation({
    mutationFn: () => congesApi.reprise.reconstruire(),
    onSuccess: (r) => {
      toast.success(`${r.bascule} mouvement(s) de bascule, ${r.acquisition} crédit(s) mensuel(s).`);
      onFait();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reconstruction impossible.'),
  });

  return (
    <Section
      titre="2. Reconstruire l’acquisition et les RTT 2026"
      description="Les congés acquis mois par mois et le crédit de RTT se recalculent : rien à saisir. Ces deux traitements tournent déjà chaque nuit ; ce bouton évite d’attendre. Relançable sans risque de double crédit."
      actions={
        <BoutonSecondaire onClick={() => lancer.mutate()} disabled={lancer.isPending}>
          {lancer.isPending ? 'Reconstruction…' : 'Reconstruire maintenant'}
        </BoutonSecondaire>
      }
    >
      <p className="text-sm text-slate-600">
        À lancer <strong>après</strong> les reports : la bascule et l’acquisition s’appuient sur
        eux.
      </p>
    </Section>
  );
}

// ─── 3. Import des absences ───────────────────────────────────────────────

function EtapeImport({ total, onFait }: { total: number; onFait: () => void }) {
  const toast = useToast();
  const [contenu, setContenu] = useState<string | null>(null);
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [analyse, setAnalyse] = useState<AnalyseImport | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const analyser = useMutation({
    mutationFn: (texte: string) => congesApi.reprise.analyser(texte),
    onSuccess: (a) => {
      setAnalyse(a);
      // La proposition sert de point de départ : elle reste modifiable, et
      // tout libellé non rapproché reste vide — donc bloquant.
      const initial: Record<string, string> = {};
      for (const [k, v] of Object.entries(a.mapping_propose)) if (v) initial[k] = v;
      setMapping(initial);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Lecture du fichier impossible.'),
  });

  const executer = useMutation({
    mutationFn: () => congesApi.reprise.executer(contenu!, mapping),
    onSuccess: (r) => {
      toast.success(
        `${r.importees} absence(s) importée(s)${r.deja ? `, ${r.deja} déjà présente(s)` : ''}.`,
      );
      setAnalyse(null);
      setContenu(null);
      setNomFichier(null);
      onFait();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Import impossible.'),
  });

  const annuler = useMutation({
    mutationFn: () => congesApi.reprise.annulerImport('tous'),
    onSuccess: (r) => {
      toast.success(`${r.demandes} absence(s) et ${r.mouvements} mouvement(s) supprimés.`);
      onFait();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Suppression impossible.'),
  });

  // Le rapprochement affiché est RECALCULÉ à chaque changement de mapping :
  // corriger un type doit faire disparaître les lignes bloquantes tout de
  // suite, sinon on relance l'import sans savoir si on a corrigé assez.
  const lignesAvecMapping = useMemo(() => {
    if (!analyse) return [];
    return analyse.lignes.map((l) => {
      const typeId = mapping[l.typeAirtable] ?? null;
      const anomalies = l.anomalies.filter((a) => !a.startsWith('Type «'));
      if (!typeId && l.typeAirtable) {
        anomalies.push(`Type « ${l.typeAirtable} » non rapproché.`);
      }
      return {
        ...l,
        type_gertrude_id: typeId,
        anomalies,
        bloquante:
          !l.debut || !l.fin || !l.salarie_id || !typeId || l.joursCalcules <= 0 ||
          anomalies.some((a) => a.startsWith('Doublon') || a.startsWith('Aucune fiche')),
      };
    });
  }, [analyse, mapping]);

  const bloquantes = lignesAvecMapping.filter((l) => l.bloquante);
  const signalees = lignesAvecMapping.filter((l) => !l.bloquante && l.anomalies.length > 0);

  const lire = (fichier: File) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const texte = String(lecteur.result ?? '');
      setContenu(texte);
      setNomFichier(fichier.name);
      analyser.mutate(texte);
    };
    // UTF-8 explicite : un export Excel en Windows-1252 rendrait « Congés
    // payés » illisible, et le rapprochement des types échouerait sans qu'on
    // comprenne pourquoi.
    lecteur.readAsText(fichier, 'utf-8');
  };

  return (
    <Section
      titre="3. Importer les absences 2026"
      description="L’export Airtable, en une seule fois. Chaque ligne devient une absence approuvée, sans notification ni marqueur de paie : tout a déjà été traité dans Airtable."
      actions={
        total > 0 ? (
          <BoutonSecondaire
            onClick={() => {
              if (confirm(`Supprimer les ${total} absences importées et leurs décomptes ?`)) {
                annuler.mutate();
              }
            }}
            disabled={annuler.isPending}
            className="!text-red-700 !ring-red-200"
          >
            Annuler l’import ({total})
          </BoutonSecondaire>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Fichier CSV</span>
          <input
            type="file"
            accept=".csv,text/csv"
            className="block w-full text-sm text-slate-600 file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:text-sm file:font-medium file:text-slate-700"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) lire(f);
            }}
          />
          {nomFichier && <span className="mt-1 block text-xs text-slate-500">{nomFichier}</span>}
        </label>

        {analyser.isPending && <p className="text-sm text-slate-500">Lecture du fichier…</p>}

        {analyse?.erreurs.length ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {analyse.erreurs.map((e, i) => (
              <p key={i}>{e}</p>
            ))}
          </div>
        ) : null}

        {analyse && analyse.types_airtable.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Rapprochement des types ({analyse.types_airtable.length})
            </h3>
            <p className="mb-2 text-sm text-slate-600">
              Chaque libellé Airtable doit désigner un type Gertrude. Aucune approximation n’est
              faite : ce qui reste vide bloque l’import.
            </p>
            <div className="space-y-2">
              {analyse.types_airtable.map((t) => (
                <div key={t} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-48 text-sm text-slate-800">{t}</span>
                  <span className="text-slate-400">→</span>
                  <Selection
                    value={mapping[t] ?? ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [t]: e.target.value }))}
                    className={`max-w-80 ${mapping[t] ? '' : 'ring-amber-400'}`}
                  >
                    <option value="">Choisir</option>
                    {analyse.types_gertrude.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.libelle}
                      </option>
                    ))}
                  </Selection>
                </div>
              ))}
            </div>
          </div>
        )}

        {analyse && (
          <>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                {lignesAvecMapping.length} ligne(s)
              </span>
              {bloquantes.length > 0 && (
                <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">
                  {bloquantes.length} bloquante(s)
                </span>
              )}
              {signalees.length > 0 && (
                <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
                  {signalees.length} à vérifier
                </span>
              )}
            </div>

            <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">Ligne</th>
                    <th className="px-3 py-2 font-medium">Salarié</th>
                    <th className="px-3 py-2 font-medium">Période</th>
                    <th className="px-3 py-2 text-right font-medium">Jours</th>
                    <th className="px-3 py-2 font-medium">À vérifier</th>
                  </tr>
                </thead>
                <tbody>
                  {lignesAvecMapping.map((l) => (
                    <tr
                      key={l.numero}
                      className={`border-t border-slate-100 ${
                        l.bloquante ? 'bg-red-50' : l.anomalies.length ? 'bg-amber-50' : ''
                      }`}
                    >
                      <td className="px-3 py-1.5 text-slate-500">{l.numero}</td>
                      <td className="px-3 py-1.5 text-slate-800">{l.salarie_nom ?? l.email}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-slate-600">
                        {l.debut ?? '?'} → {l.fin ?? '?'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                        {jours(l.joursCalcules)}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-slate-600">
                        {l.anomalies.join(' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-3">
              <BoutonPrincipal
                onClick={() => executer.mutate()}
                disabled={executer.isPending || bloquantes.length > 0 || !contenu}
              >
                {executer.isPending
                  ? 'Import en cours…'
                  : `Importer ${lignesAvecMapping.length} absence(s)`}
              </BoutonPrincipal>
              {bloquantes.length > 0 && (
                <span className="text-sm text-red-700">
                  Corrigez les {bloquantes.length} ligne(s) bloquante(s) ; rien ne s’importe
                  partiellement.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

// ─── 4. Vérification croisée ──────────────────────────────────────────────

function EtapeControle({
  salaries,
  arreteAu,
  onFait,
}: {
  salaries: SalarieReprise[];
  arreteAu: string | null;
  onFait: () => void;
}) {
  const toast = useToast();
  const [edition, setEdition] = useState(false);
  const [date, setDate] = useState(arreteAu ?? '');
  const [refs, setRefs] = useState<Record<string, { cp: number | null; rtt: number | null }>>(() => {
    const initial: Record<string, { cp: number | null; rtt: number | null }> = {};
    for (const s of salaries) {
      initial[s.id] = {
        cp: s.controle?.reference_cp ?? null,
        rtt: s.controle?.reference_rtt ?? null,
      };
    }
    return initial;
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      congesApi.reprise.references(
        date,
        salaries
          .filter((s) => refs[s.id]?.cp !== null || refs[s.id]?.rtt !== null)
          .map((s) => ({ salarie_id: s.id, cp: refs[s.id]?.cp ?? 0, rtt: refs[s.id]?.rtt ?? 0 })),
      ),
    onSuccess: () => {
      toast.success('Soldes de référence enregistrés.');
      setEdition(false);
      onFait();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.'),
  });

  const annuler = () => {
    setEdition(false);
    setDate(arreteAu ?? '');
    const initial: Record<string, { cp: number | null; rtt: number | null }> = {};
    for (const s of salaries) {
      initial[s.id] = { cp: s.controle?.reference_cp ?? null, rtt: s.controle?.reference_rtt ?? null };
    }
    setRefs(initial);
  };

  const ecarts = salaries.filter(
    (s) => (s.controle?.ecart_cp ?? 0) !== 0 || (s.controle?.ecart_rtt ?? 0) !== 0,
  );

  return (
    <Section
      titre="4. Vérification croisée des soldes"
      description="Les soldes NETS reconstruits par Gertrude, face à vos soldes Airtable du jour. Un écart signifie qu’un salarié aura un solde faux : à expliquer avant la mise en service."
      teinte={ecarts.length > 0 ? 'corail' : 'vert'}
      actions={
        !edition ? (
          <BoutonSecondaire type="button" onClick={() => setEdition(true)}>
            Modifier
          </BoutonSecondaire>
        ) : (
          <div className="flex gap-2">
            <BoutonSecondaire type="button" onClick={annuler}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              onClick={() => enregistrer.mutate()}
              disabled={enregistrer.isPending || !date}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {/* Ce qu'il y a à saisir, dit sans ambiguïté : la confusion entre un
            solde net et un cumul d'acquis rend toute la comparaison muette. */}
        <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          À saisir dans les colonnes « Airtable » : <strong>votre solde Airtable NET du jour</strong>
          {' '}- ce qu’il reste au salarié, congés déjà pris déduits. <strong>Pas</strong> un cumul
          de droits acquis. C’est le net qui teste quelque chose : le report vient de vous et
          l’acquisition est la même pour tout le monde, alors qu’une absence mal importée ou mal
          décomptée ne se voit que sur le solde net.
        </p>

        <label className="block max-w-xs">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Soldes Airtable relevés le
          </span>
          {edition ? (
            <Saisie type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          ) : (
            <ValeurCalculee>{date ? date.split('-').reverse().join('/') : '-'}</ValeurCalculee>
          )}
        </label>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-slate-600">
              {/* Deux bandeaux : à gauche d'où vient le solde, à droite la
                  comparaison. C'est la décomposition qui désigne la colonne
                  fautive dès qu'un écart apparaît. */}
              <tr className="text-xs uppercase tracking-wide text-slate-400">
                <th className="py-1 pr-4" />
                <th className="py-1 pr-4 font-medium" colSpan={4}>
                  D’où vient le solde
                </th>
                <th className="py-1 pr-4 font-medium" colSpan={4}>
                  Congés payés : comparaison
                </th>
                <th className="py-1 pr-4 font-medium" colSpan={3}>
                  RTT : comparaison
                </th>
              </tr>
              <tr>
                <th className="py-2 pr-4 font-medium">Salarié</th>
                <th className="py-2 pr-4 font-medium">Report</th>
                <th className="py-2 pr-4 font-medium">Acquis</th>
                <th className="py-2 pr-4 font-medium">Ancienneté</th>
                <th className="py-2 pr-4 font-medium">Absences</th>
                <th className="py-2 pr-4 font-medium">CP net</th>
                <th className="py-2 pr-4 font-medium">CP Airtable</th>
                <th className="py-2 pr-4 font-medium">Écart</th>
                <th className="py-2 pr-4 font-medium">RTT crédit</th>
                <th className="py-2 pr-4 font-medium">RTT pris</th>
                <th className="py-2 pr-4 font-medium">RTT net</th>
                <th className="py-2 pr-4 font-medium">RTT Airtable</th>
                <th className="py-2 pr-4 font-medium">Écart</th>
              </tr>
            </thead>
            <tbody>
              {salaries
                .filter((s) => s.compteurs_ouverts)
                .map((s) => {
                  const d = s.decomposition;
                  const ecartCp = s.controle?.ecart_cp ?? null;
                  const ecartRtt = s.controle?.ecart_rtt ?? null;
                  const classeEcart = (v: number | null) =>
                    v === null
                      ? 'text-slate-400'
                      : v === 0
                        ? 'text-emerald-700'
                        : 'text-red-700 font-semibold';
                  return (
                    <tr key={s.id} className="border-t border-slate-100">
                      <td className="whitespace-nowrap py-2 pr-4 text-slate-800">
                        {s.prenom} {s.nom}
                      </td>
                      {/* La décomposition, en retrait : elle explique, elle ne
                          se compare pas. */}
                      <td className="py-2 pr-4 tabular-nums text-slate-500">{jours(d?.report)}</td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">{jours(d?.acquis)}</td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">
                        {jours(d?.anciennete)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">{jours(d?.absences)}</td>
                      {/* Le solde NET : c'est lui qu'on compare. */}
                      <td className="py-2 pr-4 tabular-nums font-semibold text-slate-800">
                        {jours(s.controle?.reconstruit_cp)}
                      </td>
                      <td className="py-2 pr-4">
                        {edition ? (
                          <SaisieNombre
                            nullable
                            valeur={refs[s.id]?.cp ?? null}
                            onValeur={(v) =>
                              setRefs((r) => ({ ...r, [s.id]: { ...r[s.id]!, cp: v } }))
                            }
                            inputMode="decimal"
                            className="w-24"
                          />
                        ) : (
                          <ValeurCalculee>{jours(refs[s.id]?.cp ?? null)}</ValeurCalculee>
                        )}
                      </td>
                      <td className={`py-2 pr-4 tabular-nums ${classeEcart(ecartCp)}`}>
                        {ecartCp === null ? '-' : jours(ecartCp)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">
                        {jours(d?.rttCredit)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">{jours(d?.rttPris)}</td>
                      <td className="py-2 pr-4 tabular-nums font-semibold text-slate-800">
                        {jours(s.controle?.reconstruit_rtt)}
                      </td>
                      <td className="py-2 pr-4">
                        {edition ? (
                          <SaisieNombre
                            nullable
                            valeur={refs[s.id]?.rtt ?? null}
                            onValeur={(v) =>
                              setRefs((r) => ({ ...r, [s.id]: { ...r[s.id]!, rtt: v } }))
                            }
                            inputMode="decimal"
                            className="w-24"
                          />
                        ) : (
                          <ValeurCalculee>{jours(refs[s.id]?.rtt ?? null)}</ValeurCalculee>
                        )}
                      </td>
                      <td className={`py-2 pr-4 tabular-nums ${classeEcart(ecartRtt)}`}>
                        {ecartRtt === null ? '-' : jours(ecartRtt)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-500">
          Report + Acquis + Ancienneté + Absences = CP net. Les absences sont négatives : elles se
          retranchent. Un écart sur le net se localise en remontant cette ligne (un mois
          d’acquisition manquant, un report mal recopié, ou une absence absente de l’export).
        </p>

        {ecarts.length === 0 ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Aucun écart. Les soldes nets reconstruits correspondent à Airtable ; la mise en service
            peut se faire.
          </p>
        ) : (
          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            {ecarts.length} salarié(s) présentent un écart. À expliquer avant la mise en service :
            une absence oubliée dans l’export, un report mal recopié, ou une modalité de temps de
            travail manquante sur la fiche.
          </p>
        )}
      </div>
    </Section>
  );
}
